import React, { useEffect, useState } from 'react';
import './AppConfig.css';
import {
  FaEdit, FaSave, FaTimes
} from 'react-icons/fa';

const API = import.meta.env.VITE_API_URL;
const DROPBOX_SIGN_ROWS = [
  { key: 'dropbox_sign_api_key', value: '' },
  { key: 'dropbox_sign_client_id', value: '' },
  { key: 'dropbox_sign_template_id', value: '' },
  { key: 'dropbox_sign_signer_role', value: 'Client' },
  { key: 'dropbox_sign_test_mode', value: '1' },
  { key: 'dropbox_sign_contract_title', value: '{{client_name}} Services Agreement' },
  { key: 'dropbox_sign_subject', value: 'Please sign your Easy3PL agreement' },
  { key: 'dropbox_sign_message', value: 'Please review and sign the attached agreement for {{client_name}}.' },
  { key: 'dropbox_sign_custom_fields_map', value: '{"client_name":"name","client_contact":"contact","client_email":"email","billing_contact":"billingcontact","billing_email":"billingemail","effective_date":"today"}' },
];

const NAV_TOGGLE_ROWS = [
  { key: 'showorders', value: '1' },
  { key: 'showfreight', value: '1' },
  { key: 'showinvoicing', value: '1' },
  { key: 'showinvoices', value: '1' },
  { key: 'showclients', value: '1' },
  { key: 'showwarehousefees', value: '1' },
];

export default function AppConfig() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingKey, setSavingKey] = useState(null);
  const [nonEditableKeys, setNonEditableKeys] = useState(new Set());
  const [images, setImages] = useState({}); // legacy; not used for list rendering
  const [imgVersion, setImgVersion] = useState({}); // cache-buster per key
  const [brokenImages, setBrokenImages] = useState({}); // key -> true when /image 404s
  const IMAGE_KEYS = React.useMemo(() => new Set(['company_logo']), []);
  const candidateImageKeys = React.useMemo(() => {
    const out = new Set([...IMAGE_KEYS]);
    for (const r of rows) {
      const k = String(r.key || '');
      const v = r.value;
      if (typeof v === 'string' && (v.startsWith('data:image/') || /\.(png|jpg|jpeg|gif|webp|svg)(\?.*)?$/i.test(v))) {
        out.add(k);
      }
    }
    return out;
  }, [rows, IMAGE_KEYS]);
  const [imgEditOpen, setImgEditOpen] = useState(false);
  const [imgEditKey, setImgEditKey] = useState('');
  const [imgFile, setImgFile] = useState(null);
  const [imgPreviewUrl, setImgPreviewUrl] = useState('');
  const [imgUploading, setImgUploading] = useState(false);
  const [imgError, setImgError] = useState('');
  // Test email state
  const defaultEmail = (() => { try { return (JSON.parse(localStorage.getItem('user')||'{}')?.email) || ''; } catch { return ''; } })();
  const [testTo, setTestTo] = useState(defaultEmail);
  const [testSubject, setTestSubject] = useState('Easy3PL Test Email');
  const [testMsg, setTestMsg] = useState('This is a test email from Easy3PL using App Configuration SMTP settings.');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/appconfig`);
      const data = await res.json();
      const base = Object.entries(data).map(([k, v]) => ({ key: k, value: v }));
      const seen = new Set(base.map((row) => row.key));
      const merged = [...base];
      for (const row of [...NAV_TOGGLE_ROWS, ...DROPBOX_SIGN_ROWS]) {
        if (!seen.has(row.key)) merged.push({ ...row });
      }
      merged.sort((a, b) => String(a.key || '').localeCompare(String(b.key || '')));
      setRows(merged);
    } catch (e) {
      console.error('Failed to fetch appconfig', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // No prefetch for list rendering; thumbnails reference API URL directly.

  const startEdit = (row) => {
    if (nonEditableKeys.has(row.key)) {
      alert('This key is not editable.');
      return;
    }
    setEditingKey(row.key);
    setEditValue(row.value ?? '');
  };

  const isRowImage = (row) => candidateImageKeys.has(row.key) && !brokenImages[row.key];

  const openImageEdit = (row) => {
    if (!candidateImageKeys.has(row.key)) { startEdit(row); return; }
    if (nonEditableKeys.has(row.key)) {
      alert('This key is not editable.');
      return;
    }
    setImgError('');
    setImgFile(null);
    setImgEditKey(row.key);
    setImgEditOpen(true);
  };

  const closeImageEdit = () => {
    setImgEditOpen(false);
    setImgEditKey('');
    if (imgPreviewUrl) { try { URL.revokeObjectURL(imgPreviewUrl); } catch {} }
    setImgFile(null);
    setImgPreviewUrl('');
    setImgError('');
  };

  const saveImageEdit = async () => {
    if (!imgEditKey || !imgFile) { setImgError('Please choose an image file.'); return; }
    setImgUploading(true);
    setImgError('');
    try {
      const fd = new FormData();
      fd.append('image', imgFile);
      const res = await fetch(`${API}/api/appconfig/${encodeURIComponent(imgEditKey)}/image`, {
        method: 'PUT',
        body: fd,
      });
      if (!res.ok) {
        if (res.status === 403) {
          setNonEditableKeys(prev => new Set([...prev, imgEditKey]));
          throw new Error('This key is not editable');
        }
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Upload failed');
      }
      // Refresh just this image URL
      // bump version to bust cache on image URLs
      setImgVersion(prev => ({ ...prev, [imgEditKey]: (prev[imgEditKey] || 0) + 1 }));
      setBrokenImages(prev => ({ ...prev, [imgEditKey]: false }));
      closeImageEdit();
    } catch (e) {
      setImgError(e.message || 'Failed to upload');
    } finally {
      setImgUploading(false);
    }
  };

  // --- Topbar title (set after render; clear on unmount) ---
  useEffect(() => {
    const subtitle = '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'App Configuration', subtitle }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, []);

  const cancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
  };

	  const saveEdit = async (row) => {
	    setSavingKey(row.key);
	    try {
	      const res = await fetch(`${API}/api/appconfig/${row.key}`, {
	        method: 'PUT',
	        headers: { 'Content-Type': 'application/json' },
	        body: JSON.stringify({ value: editValue }),
	      });
      if (!res.ok) {
        if (res.status === 403) {
          setNonEditableKeys(prev => new Set([...prev, row.key]));
          throw new Error('This key is not editable');
        }
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Save failed');
      }
	      setRows(prev =>
	        prev.map(r => (r.key === row.key ? { ...r, value: editValue } : r))
	      );
	      try {
	        window.dispatchEvent(new CustomEvent('appconfig:updated', { detail: { key: row.key, value: editValue } }));
	      } catch {}
	      setEditingKey(null);
	      setEditValue('');
	    } catch (e) {
	      alert(e.message);
	    } finally {
      setSavingKey(null);
    }
  };

  const renderValue = (row) => {
    // For image-like keys, show thumbnail from API with cache-busting, hide if broken
    if (candidateImageKeys.has(row.key) && !brokenImages[row.key]) {
      const src = `${API}/api/appconfig/${encodeURIComponent(row.key)}/image?v=${imgVersion[row.key] || 0}`;
      return (
        <a href={src} target="_blank" rel="noopener noreferrer">
          <img
            src={src}
            alt={row.key}
            style={{ maxHeight: 80, borderRadius: 4, display: brokenImages[row.key] ? 'none' : 'inline-block' }}
            onError={() => setBrokenImages(prev => ({ ...prev, [row.key]: true }))}
          />
        </a>
      );
    }
    // Show base64 preview if present (non-blob)
    if (typeof row.value === 'string' && row.value.startsWith('data:image/')) {
      return (
        <a href={row.value} target="_blank" rel="noopener noreferrer">
          <img src={row.value} alt={row.key} style={{ maxHeight: 80, borderRadius: 4 }} />
        </a>
      );
    }
    return row.value ?? '';
  };

  return (
    <>
      <div className="appconfig-wrapper">
        <div className="appconfig-tablecol">
          {loading ? (
            <div className="loading">Loading…</div>
          ) : (
            <>
              <table className="appconfig-table appconfig-table--skinned">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Actions</th>
                    <th>Key</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length ? (
                    rows.map((row) => (
                      <tr key={row.key} className={isRowImage(row) ? 'has-image' : ''}>
                        <td className="actions">
                          {editingKey === row.key ? (
                            <>
                              <button
                                className="mini-btn primary"
                                title="Save"
                                disabled={savingKey === row.key}
                                onClick={() => saveEdit(row)}
                              >
                                <FaSave size={14} />
                              </button>
                              <button
                                className="mini-btn"
                                title="Cancel"
                                onClick={cancelEdit}
                              >
                                <FaTimes size={14} />
                              </button>
                            </>
                          ) : (
                            <button
                              className="mini-btn"
                              title={nonEditableKeys.has(row.key) ? 'Not editable' : (isRowImage(row) ? 'Change Image' : 'Edit')}
                              disabled={nonEditableKeys.has(row.key)}
                              onClick={() => (isRowImage(row) ? openImageEdit(row) : startEdit(row))}
                            >
                              <FaEdit size={14} />
                            </button>
                          )}
                        </td>
                        <td className="nowrap">{row.key}</td>
                        <td>
                          {editingKey === row.key ? (
                            <input
                              value={editValue}
                              onChange={e => setEditValue(e.target.value)}
                              className="edit-input"
                            />
                          ) : (
                            renderValue(row)
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} style={{ textAlign: 'center', opacity: 0.7 }}>
                        No config values
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="test-email-card">
                <div className="test-email-title">Test SMTP Email</div>
                <div className="row">
                  <label>
                    <span>Test Email To</span>
                    <input type="email" value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="you@example.com" />
                  </label>
                  <label>
                    <span>Subject</span>
                    <input type="text" value={testSubject} onChange={e => setTestSubject(e.target.value)} />
                  </label>
                </div>
                <label className="span">
                  <span>Message</span>
                  <textarea rows={3} value={testMsg} onChange={e => setTestMsg(e.target.value)} />
                </label>
                <div className="actions test-email-actions">
                  <button
                    className="btn"
                    disabled={testSending || !testTo}
                    onClick={async () => {
                      setTestSending(true); setTestResult('');
                      try {
                        const res = await fetch(`${API}/api/appconfig/test-email`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ to: testTo, subject: testSubject, message: testMsg })
                        });
                        const j = await res.json().catch(()=>({}));
                        if (!res.ok || j?.error) throw new Error(j?.error || 'Failed to send');
                        setTestResult('Sent!');
                      } catch (e) {
                        setTestResult(e.message || 'Failed');
                      } finally {
                        setTestSending(false);
                      }
                    }}
                  >{testSending ? 'Sending…' : 'Send Test Email'}</button>
                  {testResult ? <span className="test-result">{testResult}</span> : null}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {imgEditOpen && (
        <div className="eml-modal__overlay" role="dialog" aria-modal="true" aria-labelledby="img-edit-title" onClick={(e) => { if (e.target.classList.contains('eml-modal__overlay')) closeImageEdit(); }}>
          <div className="eml-modal" style={{ maxWidth: 520 }}>
            <div className="eml-modal__header">
              <h2 id="img-edit-title">Update Image: {imgEditKey}</h2>
              <button className="eml-modal__close" onClick={closeImageEdit} aria-label="Close">✕</button>
            </div>
            <div className="eml-modal__body">
              {imgError ? <div className="eml-modal__error" role="alert">{imgError}</div> : null}
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 12, opacity: .8, marginBottom: 6 }}>Current</div>
                  {(() => {
                    const apiSrc = `${API}/api/appconfig/${encodeURIComponent(imgEditKey)}/image?v=${imgVersion[imgEditKey] || 0}`;
                    return (
                      <img
                        src={apiSrc}
                        alt={imgEditKey}
                        style={{ maxHeight: 100, borderRadius: 6, border: '1px solid #444' }}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    );
                  })()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, opacity: .8, marginBottom: 6 }}>Select replacement</div>
                  <div
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={(e) => {
                      e.preventDefault(); e.stopPropagation();
                      const f = e.dataTransfer?.files?.[0];
                      if (f && f.type?.startsWith('image/')) {
                        if (imgPreviewUrl) { try { URL.revokeObjectURL(imgPreviewUrl); } catch {} }
                        setImgFile(f);
                        setImgPreviewUrl(URL.createObjectURL(f));
                      }
                    }}
                    style={{
                      border: '1px dashed #555', borderRadius: 6, padding: 12, textAlign: 'center', marginBottom: 8,
                      background: 'var(--input-background)', color: 'var(--text-color)'
                    }}
                  >
                    Drag & drop image here
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      if (f) {
                        if (imgPreviewUrl) { try { URL.revokeObjectURL(imgPreviewUrl); } catch {} }
                        setImgFile(f);
                        setImgPreviewUrl(URL.createObjectURL(f));
                      } else {
                        if (imgPreviewUrl) { try { URL.revokeObjectURL(imgPreviewUrl); } catch {} }
                        setImgFile(null);
                        setImgPreviewUrl('');
                      }
                    }}
                  />
                  {imgPreviewUrl ? (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 12, opacity: .8, marginBottom: 6 }}>Preview</div>
                      <img src={imgPreviewUrl} alt="preview" style={{ maxHeight: 120, borderRadius: 6, border: '1px solid #444' }} />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="eml-modal__footer">
              <button className="btn" onClick={saveImageEdit} disabled={imgUploading || !imgFile}>{imgUploading ? 'Uploading…' : 'Save'}</button>
              <button className="btn btn-secondary" onClick={closeImageEdit} disabled={imgUploading}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
